import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from '../context/shared/ThemeContext';
import DemoCard from '../components/common/DemoCard';
import DemoButton from '../components/common/DemoButton';

const HomeModern: React.FC = () => {
  const { themeClasses } = useTheme();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: '#e9effc' }}>
      {/* Header */}
      <div className="max-w-2xl w-full text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold mb-2" style={{ color: '#22223b' }}>
          <span role="img" aria-label="brain">🧠</span> Mentra
        </h1>
        <p className="text-lg md:text-xl text-gray-600 mb-6">Your AI-powered mental health companion</p>
      </div>

      {/* Info Box */}
      <DemoCard className="max-w-2xl w-full bg-yellow-50 border-yellow-300 mb-8 text-left">
        <div className="flex items-start mb-2">
          <span className="text-2xl mr-2">⚠️</span>
          <div>
            <span className="font-semibold text-yellow-900">The AI Mental Health Landscape</span>
          </div>
        </div>
        <div className="ml-8">
          <div className="mb-1">
            <span className="font-bold text-yellow-900">Over 25% of adults now use AI for emotional support</span>
            <span className="text-yellow-900">—often without oversight.</span>
          </div>
          <div className="text-yellow-900 mb-3 text-sm">Unregulated chatbots have already been linked to harm, especially in teens.</div>
          <div className="border-l-4 border-red-400 bg-red-50 p-3 rounded mb-1">
            <div className="font-semibold text-red-700 mb-1 flex items-center">
              <span className="mr-1">🛡️</span> Mentra offers a safer path
            </div>
            <div className="text-red-800 text-sm">Designed for therapists, it blends ethical AI, symbolic insight, and clinical control—so you stay at the center of care.</div>
          </div>
        </div>
      </DemoCard>

      {/* Features Card */}
      <DemoCard className="max-w-xl w-full mb-8">
        <ul className="mb-6 space-y-2 text-left">
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            AI-powered chat with therapeutic guidance
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            Emotional radar chart with 9 dimensions
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            Symbolic analysis and pattern recognition
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            Memory anchors for meaningful moments
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            Therapist integration and oversight
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            HIPAA-compliant data storage
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            Personalized insights and recommendations
          </li>
          <li className="flex items-center text-base text-gray-800">
            <span className="text-green-500 text-xl mr-2">✅</span>
            Progress tracking and analytics
          </li>
        </ul>
        <div className="flex flex-col md:flex-row gap-4 justify-center mb-2">
          <Link to="/chat">
            <DemoButton variant="primary" className="w-full md:w-auto">
              <span className="mr-2">💬</span>
              Start Chatting
            </DemoButton>
          </Link>
          <Link to="/dashboard">
            <DemoButton variant="secondary" className="w-full md:w-auto">
              <span className="mr-2">📊</span>
              View Dashboard
            </DemoButton>
          </Link>
        </div>
        <div className="text-center text-gray-500 text-sm mt-2">
          Begin your mental health journey with AI guidance
        </div>
      </DemoCard>

      {/* How It Works */}
      <DemoCard className="max-w-4xl w-full mb-8">
        <h2 className="text-2xl font-bold text-gray-900  mb-6 text-center">
          How Mentra Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100  rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💬</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900  mb-2">
              1. Chat with AI
            </h3>
            <p className="text-gray-600  text-sm">
              Have natural conversations with our AI that understands mental health context and provides therapeutic guidance.
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100  rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📊</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900  mb-2">
              2. Track Patterns
            </h3>
            <p className="text-gray-600  text-sm">
              Our symbolic analysis identifies emotional patterns and tracks your progress across 9 key dimensions.
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100  rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🧠</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900  mb-2">
              3. Gain Insights
            </h3>
            <p className="text-gray-600  text-sm">
              Receive personalized insights, recommendations, and track your mental health journey with professional oversight.
            </p>
          </div>
        </div>
      </DemoCard>

      {/* Call to Action */}
      <DemoCard className="max-w-2xl w-full text-center">
        <h2 className="text-2xl font-bold text-gray-900  mb-4">
          Ready to Start Your Journey?
        </h2>
        <p className="text-gray-600  mb-6">
          Join thousands of users who are already using Mentra to improve their mental health with AI guidance.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/signup">
            <DemoButton variant="primary" size="lg">
              Get Started Free
            </DemoButton>
          </Link>
          <Link to="/login">
            <DemoButton variant="secondary" size="lg">
              Sign In
            </DemoButton>
          </Link>
        </div>
      </DemoCard>
    </div>
  );
};

export default HomeModern;
